import { useRef, useCallback, useEffect, useMemo } from "react";
import {
  createIntersectionCallback,
  checkIntersectionObserverSupport,
  resetChildAccessibility,
} from "../utils";
import { setIntersectionObserver } from "../../../utils/setIntersectionObserver";
import { INTERSECTION_THRESHOLDS } from "../constants";

const useChildAccessibility = (listRef, isNotMobileViewport) => {
  const observerRef = useRef(null);
  const isObserverActiveRef = useRef(false);

  const intersectionCallback = useMemo(() => createIntersectionCallback(), []);

  const resetObserver = useCallback(() => {
    if (isObserverActiveRef.current && listRef.current) {
      resetChildAccessibility(listRef.current, observerRef.current);
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      isObserverActiveRef.current = false;
    }
  }, [listRef]);

  const observeChildren = useCallback(() => {
    if (!listRef.current || !observerRef.current) return;

    const children = listRef.current.children;

    Array.from(children).forEach((child) => {
      observerRef.current.observe(child);
    });
    isObserverActiveRef.current = true;
  }, [listRef]);

  const createObserver = useCallback(() => {
    if (!observerRef.current && listRef.current) {
      const children = listRef.current.children;
      const firstChild = children && children.length > 0 ? children[0] : null;

      observerRef.current = setIntersectionObserver({
        element: firstChild,
        options: {
          root: listRef.current,
          rootMargin: "0px",
          threshold: INTERSECTION_THRESHOLDS,
        },
        handleIntersectionData: (entry) => intersectionCallback([entry]),
        observeIntersectionOnce: false,
        debounceMs: 0,
        autoObserve: true,
      });
    }
  }, [listRef, intersectionCallback]);

  const shouldUseObserver = useCallback(
    () => isNotMobileViewport && checkIntersectionObserverSupport(),
    [isNotMobileViewport]
  );

  const updateExistingObserver = useCallback(() => {
    if (observerRef.current) {
      // Don’t disconnect to avoid dropping debounced initial callbacks.
      // Simply (re)observe current children; duplicate observe calls are ignored by the browser.
      observeChildren();
    }
  }, [observeChildren]);

  const initializeObserver = useCallback(() => {
    createObserver();
    if (observerRef.current) {
      observeChildren();
    }
  }, [createObserver, observeChildren]);

  const handleChildAccessibility = useCallback(() => {
    if (!listRef.current) return;

    if (!shouldUseObserver()) {
      resetObserver();

      return;
    }

    if (isObserverActiveRef.current) {
      updateExistingObserver();
    } else {
      initializeObserver();
    }
  }, [
    listRef,
    shouldUseObserver,
    resetObserver,
    updateExistingObserver,
    initializeObserver,
  ]);

  useEffect(
    () => () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    },
    []
  );

  return { handleChildAccessibility, observerRef };
};

export default useChildAccessibility;
